<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch, type PropType } from 'vue';
import type { ChatStatusGroup } from '@/composables/useChatStatusGroups';
import {
  isGenericThinkingLabel,
  nextPlayfulThinkingMessage,
  PLAYFUL_THINKING_ENABLED,
} from '@/utils/chat/thinkingMessages';
import GaiaStatusIndicator from '../../base/GaiaStatusIndicator.vue';
import GaiaText from '../../base/GaiaText.vue';

const props = defineProps({
  /**
   * What the run is doing, from its `chat_status` frames. Finished groups are
   * included on purpose — a tool call can open and close between two paints,
   * and a step that only ever renders while in flight is a step nobody sees.
   * The caller decides how many finished ones to keep.
   */
  groups: {
    type: Array as PropType<ChatStatusGroup[]>,
    default: () => [],
  },
  /** Shown before the first frame arrives, when there is nothing to report yet. */
  fallback: {
    type: String,
    default: 'Thinking…',
  },
  /**
   * Replace the fallback with a rotating law-themed quip. Deliberately scoped
   * to the fallback: a real status frame always wins, because someone waiting
   * on a contract review is owed the truth about what ran.
   */
  playful: {
    type: Boolean,
    default: PLAYFUL_THINKING_ENABLED,
  },
});

/** How long a quip stays up. Long enough to read, short enough to feel alive. */
const PLAYFUL_ROTATE_MS = 3800;

const playfulMessage = ref('');
let rotateTimer: ReturnType<typeof setInterval> | null = null;

function stopRotating(): void {
  if (rotateTimer === null) return;
  clearInterval(rotateTimer);
  rotateTimer = null;
}

/**
 * A group whose label says nothing — the backend's synthetic "Thinking…" — is
 * the fallback wearing a group's clothes, so it is the other thing a quip may
 * stand in for. Real tool labels are never touched.
 */
function isPlaceholder(group: ChatStatusGroup): boolean {
  return isGenericThinkingLabel(group.label);
}

/** Only while a placeholder is actually on screen — no timer behind a real status. */
const showPlayful = computed(
  () => props.playful && (props.groups.length === 0 || props.groups.some(isPlaceholder))
);

function groupLabel(group: ChatStatusGroup): string {
  return showPlayful.value && isPlaceholder(group) ? `${playfulMessage.value}…` : group.label;
}

watch(
  showPlayful,
  visible => {
    stopRotating();
    if (!visible) return;
    playfulMessage.value = nextPlayfulThinkingMessage();
    rotateTimer = setInterval(() => {
      playfulMessage.value = nextPlayfulThinkingMessage(playfulMessage.value);
    }, PLAYFUL_ROTATE_MS);
  },
  { immediate: true }
);

onBeforeUnmount(stopRotating);

const fallbackText = computed(() =>
  showPlayful.value ? `${playfulMessage.value}…` : props.fallback
);
</script>

<template>
  <n-flex vertical :size="7" class="gaia-chat-status">
    <n-flex v-if="!props.groups.length" align="center" :size="8" :wrap="false">
      <gaia-status-indicator status="running" />
      <gaia-text size="dense" tone="faint">{{ fallbackText }}</gaia-text>
    </n-flex>

    <div v-for="group in props.groups" :key="group.id">
      <n-flex align="center" :size="8" :wrap="false">
        <gaia-status-indicator :status="group.done ? 'done' : 'running'" />
        <gaia-text size="dense" :tone="group.done ? 'faint' : 'tertiary'">
          {{ groupLabel(group) }}
        </gaia-text>
      </n-flex>
      <!-- The tool's own running commentary, capped at 3 by the composable. -->
      <!-- Details hang under their group's label, past the indicator: the dot's
           width plus the flex gap. naive has no primitive that indents to a
           sibling's content edge. -->
      <n-flex v-if="group.details.length" vertical :size="2" style="margin: 4px 0 0 15px">
        <gaia-text v-for="detail in group.details" :key="detail.id" size="meta" tone="faint">
          {{ detail.text }}
        </gaia-text>
      </n-flex>
    </div>
  </n-flex>
</template>
